import React from "react";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import Header from "../components/site/Header";
import Seo from "../components/site/Seo";
import Footer from "../components/site/Footer";
import Photo from "../components/site/Photo";
import QuoteForm from "../components/site/QuoteForm";
import { Breadcrumb, SisterServices } from "../components/site/ServiceSections";

export default function Contact() {
  return (
    <div className="min-h-screen bg-ink text-bone">
      <Seo />
      <Header />
      <Hero />
      <ContactBlock />
      <SisterServices keys={["menu", "food", "venue", "brand"]} />
      <Footer />
    </div>
  );
}

const Hero = () => (
  <section className="relative min-h-[60svh] overflow-hidden bg-ink">
    <div className="absolute inset-0">
      <Photo slug="hero-banquet-01" alt="Contact Restaurant Photographer Melbourne" priority ratio="auto" className="!relative h-full w-full" />
    </div>
    <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/55 to-ink" />

    <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10 min-h-[60svh] flex flex-col pt-24 sm:pt-28 lg:pt-32 pb-10 lg:pb-12">
      <Breadcrumb current="Contact" />

      <div className="flex-1 flex flex-col justify-center max-w-4xl">
        <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-5 sm:mb-7 font-bold">
          Contact · Direct line to the photographer
        </p>
        <h1 className="font-display tracking-editorial leading-[0.92]" style={{ fontSize: "clamp(2.5rem, 7vw, 5.5rem)" }}>
          Pick up the phone
          <br />
          or send <span className="italic font-light text-ember">a note.</span>
        </h1>
        <p className="mt-6 sm:mt-8 max-w-2xl text-base sm:text-lg md:text-xl leading-relaxed text-bone/85">
          No call centres, no assistants, no auto-replies. You speak directly
          with the photographer — usually within one business day.
        </p>
      </div>
    </div>
  </section>
);

const ContactBlock = () => (
  <section id="enquiry" className="bg-bone text-ink py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-10 lg:gap-16">
        <div className="col-span-12 lg:col-span-5">
          <p className="text-xs uppercase tracking-wider2 text-ember mb-6 font-bold">
            Three ways to reach us
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2rem, 4.5vw, 4rem)" }}>
            Direct lines.
            <br />
            <span className="italic font-light text-ember">No layers.</span>
          </h2>

          <div className="mt-10 space-y-8 border-t border-ink/15 pt-10">
            <a href="tel:0494756111" data-testid="contact-phone" className="group flex items-start gap-5">
              <Phone size={18} strokeWidth={1.6} className="text-ember mt-1" />
              <div>
                <p className="text-xs uppercase tracking-wider2 text-char-mute mb-1 font-bold">Direct phone</p>
                <p className="font-sans font-bold text-3xl tracking-tight group-hover:text-ember transition-colors">0494 756 111</p>
                <p className="mt-2 text-sm text-char-mute">Fastest path — most quotes confirmed in under five minutes.</p>
              </div>
            </a>

            <a href="mailto:hello@restaurantphotographer.com.au" data-testid="contact-email" className="group flex items-start gap-5">
              <Mail size={18} strokeWidth={1.6} className="text-ember mt-1" />
              <div>
                <p className="text-xs uppercase tracking-wider2 text-char-mute mb-1 font-bold">Email</p>
                <p className="font-display text-xl md:text-2xl tracking-editorial group-hover:text-ember transition-colors break-all">hello@restaurantphotographer.com.au</p>
                <p className="mt-2 text-sm text-char-mute">For briefs, decks, attachments, or written enquiries.</p>
              </div>
            </a>

            <div className="flex items-start gap-5">
              <MapPin size={18} strokeWidth={1.6} className="text-ember mt-1" />
              <div>
                <p className="text-xs uppercase tracking-wider2 text-char-mute mb-1 font-bold">Coverage</p>
                <p className="font-display text-xl tracking-editorial">Mobile · Servicing all Melbourne suburbs</p>
                <p className="mt-2 text-sm text-char-mute">No travel fees inside the metro area.</p>
              </div>
            </div>

            <div className="flex items-start gap-5">
              <Clock size={18} strokeWidth={1.6} className="text-ember mt-1" />
              <div>
                <p className="text-xs uppercase tracking-wider2 text-char-mute mb-1 font-bold">Hours</p>
                <p className="font-display text-xl tracking-editorial">Mon–Sat · 7am–7pm AEST</p>
                <p className="mt-2 text-sm text-char-mute">We&apos;ll always call back the next business day.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-7">
          <div className="bg-bone border border-ink/10 p-8 lg:p-12">
            <p className="text-xs uppercase tracking-wider2 text-ember mb-3 font-bold">Send a note</p>
            <h3 className="font-display text-2xl md:text-3xl tracking-editorial leading-tight mb-10">
              Tell us about your shoot.
            </h3>
            <QuoteForm source="contact" />
          </div>
        </div>
      </div>
    </div>
  </section>
);
